import React, { useEffect, useState } from 'react';
// ...existing code...

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
import { Box, Typography, Button, CircularProgress, Select, MenuItem, List, ListItem, ListItemButton, ListItemText, Divider } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import yaml from 'js-yaml';
import RoutingRulesEditorForm from './RoutingRulesEditorForm';

const GITHUB_API = 'https://api.github.com/repos/42Consulting/maptyc_config/contents';

export default function RoutingRulesEditorPage() {
  const handleDeleteRule = (key) => {
    if (window.confirm(`Are you sure you want to delete the rule "${key}"? This cannot be undone.`)) {
      const updatedYamlObj = { ...yamlObj };
      delete updatedYamlObj[key];
      setYamlObj(updatedYamlObj);
      setFileContent(yaml.dump(updatedYamlObj));
      if (selectedItem === key) setSelectedItem('');
    }
  };
  // Helper to get default structure from first rule
  const getDefaultRule = () => {
    const keys = Object.keys(yamlObj);
    if (keys.length > 0) {
      // Deep clone first rule
      return JSON.parse(JSON.stringify(yamlObj[keys[0]]));
    }
    // Fallback default
    return {
      tags: {
        lexifi_instance_name: '',
        lexifi_product_type: '',
        source: ''
      },
      classification: {
        product_type_name: ''
      }
    };
  };

  const [newRuleName, setNewRuleName] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAddRule = () => {
    const name = newRuleName.trim();
    if (!name || yamlObj[name]) return;
    setAdding(true);
    const newObj = { ...yamlObj, [name]: getDefaultRule() };
    setYamlObj(newObj);
    setFileContent(yaml.dump(newObj));
    setNewRuleName('');
    setSelectedItem(name);
    setAdding(false);
  };
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [yamlObj, setYamlObj] = useState({});
  const [selectedItem, setSelectedItem] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/yml-files`)
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) {
          setError('Failed to fetch file list');
          setFiles([]);
        } else {
          setFiles(data);
        }
      })
      .catch(() => {
        setError('Failed to fetch file list');
        setFiles([]);
      });
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.value;
    setSelectedFile(file);
    setLoading(true);
    setError(null);
    setSelectedItem('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/yml-file?filename=${encodeURIComponent(file)}`);
      const data = await res.json();
      if (data.content) {
        setFileContent(data.content);
        try {
          const obj = yaml.load(data.content) || {};
          setYamlObj(obj);
        } catch (e) {
          setYamlObj({});
        }
      } else {
        setError('Failed to fetch file content');
        setYamlObj({});
      }
    } catch (err) {
      setError('Failed to fetch file content');
      setYamlObj({});
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    if (!selectedFile) {
      setError('No file selected');
      setSaving(false);
      return;
    }
    const message = prompt('Enter a commit message for your change:', 'Update routing rules');
    if (!message) {
      setSaving(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/yml-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: selectedFile, content: fileContent, message })
      });
      const data = await res.json();
      if (data.success) {
        alert('File saved successfully!');
      } else {
        setError(data.error || 'Failed to save file');
      }
    } catch (err) {
      setError('Failed to save file');
    }
    setSaving(false);
  };

  return (
    <Box sx={{ p: 3, display: 'flex', gap: 3 }}>
      <Box sx={{ minWidth: 250, borderRight: '1px solid #eee', pr: 2 }}>
        <Typography variant="h6" gutterBottom>Routing Rule Files</Typography>
        <Select
          value={selectedFile}
          onChange={handleFileSelect}
          displayEmpty
          sx={{ minWidth: 200, mb: 2 }}
        >
          <MenuItem value="" disabled>Select a YML file</MenuItem>
          {files.map(f => (
            <MenuItem key={f.name} value={f.name}>{f.name}</MenuItem>
          ))}
        </Select>
        <Divider sx={{ mb: 2 }} />
        {loading && <CircularProgress />}
        {error && <Typography color="error">{error}</Typography>}
        {selectedFile && Object.keys(yamlObj).length > 0 && (
          <List>
            {Object.keys(yamlObj).map(key => (
              <ListItem key={key} disablePadding secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteRule(key)}
                  sx={{ color: 'error.main' }}
                >
                  <CloseIcon />
                </IconButton>
              }>
                <ListItemButton selected={selectedItem === key} onClick={() => setSelectedItem(key)}>
                  <ListItemText primary={key} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        {selectedFile && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <input
              type="text"
              placeholder="New rule name"
              value={newRuleName}
              onChange={e => setNewRuleName(e.target.value)}
              style={{ flex: 1, padding: '8px', borderRadius: 4, border: '1px solid #ccc' }}
              disabled={adding}
            />
            <Button variant="outlined" onClick={handleAddRule} disabled={!newRuleName.trim() || yamlObj[newRuleName.trim()] !== undefined || adding}>
              Add Rule
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h4" gutterBottom>Routing Rules Editor</Typography>
        {selectedItem && yamlObj[selectedItem] && (
          <RoutingRulesEditorForm
            key={selectedItem}
            initialData={yamlObj[selectedItem]}
            onSave={data => {
              // Update YAML object and fileContent
              const updatedYamlObj = { ...yamlObj, [selectedItem]: data };
              setYamlObj(updatedYamlObj);
              setFileContent(yaml.dump(updatedYamlObj));
            }}
          />
        )}
        {!selectedItem && (
          <Typography variant="body1" sx={{ mt: 2 }}>Select a routing rule from the list to edit.</Typography>
        )}
        {fileContent && selectedFile && (
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleSave} disabled={saving}>
            Save Changes
          </Button>
        )}
      </Box>
    </Box>
  );
}
